/**
 * EBIOS RM - Universal DOCX Report Generator
 * Provides reusable helpers for generating professional Word (.docx) reports.
 * Uses the same `docx` library already installed in this project.
 */

import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    Table,
    TableRow,
    TableCell,
    WidthType,
    AlignmentType,
    Header,
    Footer,
    PageNumber,
    ShadingType,
    BorderStyle,
    convertInchesToTwip,
    PageBreak,
    TableOfContents,
    ImageRun,
    StyleLevel,
    PageOrientation,
} from 'docx';

import { saveAs } from './download';

// ──────────────────────────────────────────────
// COLOR PALETTE
// ──────────────────────────────────────────────
export const DOCX_COLORS = {
    primary: '1E40AF',      // Blue-700
    secondary: '64748B',    // Slate-500
    success: '059669',      // Emerald-600
    warning: 'D97706',      // Amber-600
    danger: 'DC2626',       // Red-600
    dark: '0F172A',         // Slate-950
    light: 'F8FAFC',        // Slate-50
    white: 'FFFFFF',
    headerBg: '1E3A8A',     // Deep blue for table headers
    altRowBg: 'F1F5F9',     // Slate-100 for alternating rows
    // Normal colors (previously red accent)
    accentRed: '0F172A',            // Same as dark
    accentRedHeaderBg: '1E3A8A',    // Same as headerBg
    accentRedCellBg: '',            // No background
    accentRedAltCellBg: '',         // No background
    accentRedBorder: '1E3A8A',      // Same as headerBg
};

// ──────────────────────────────────────────────
// HELPER: COVER PAGE
// ──────────────────────────────────────────────

/**
 * Build a professional cover page for any EBIOS RM report.
 * @param {string} title          - Main document title
 * @param {string} subtitle       - Section/atelier subtitle
 * @param {string} orgName        - Organisation name
 * @param {string} analysisName   - Analysis name
 * @param {string} generatedBy    - Tool / system name
 */
export function buildCoverPage(title, subtitle, orgName, analysisName, generatedBy = 'EBIOSens', lang = 'fr') {
    const isEn = lang === 'en';
    const date = new Date().toLocaleDateString(isEn ? 'en-GB' : 'fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    return [
        // Big top spacer
        new Paragraph({ text: '', spacing: { after: 1800 } }),

        // Logo-like header bar
        new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
                new TextRun({ text: '━'.repeat(50), color: DOCX_COLORS.primary }),
            ],
            spacing: { after: 400 },
        }),

        // Main title
        new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
                new TextRun({
                    text: title,
                    bold: true,
                    size: 64,
                    color: DOCX_COLORS.primary,
                }),
            ],
            spacing: { after: 300 },
        }),

        // Subtitle
        new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
                new TextRun({
                    text: subtitle,
                    size: 32,
                    color: DOCX_COLORS.secondary,
                    italics: true,
                }),
            ],
            spacing: { after: 600 },
        }),

        // Separator
        new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
                new TextRun({ text: '━'.repeat(50), color: DOCX_COLORS.primary }),
            ],
            spacing: { after: 800 },
        }),

        // Organisation
        new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
                new TextRun({ text: isEn ? 'Organization: ' : 'Organisation : ', bold: true, size: 26 }),
                new TextRun({ text: orgName || (isEn ? 'Not specified' : 'Non sp\u00e9cifi\u00e9'), size: 26 }),
            ],
            spacing: { after: 200 },
        }),

        // Analysis name
        new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
                new TextRun({ text: isEn ? 'Analysis: ' : 'Analyse : ', bold: true, size: 26 }),
                new TextRun({ text: analysisName || (isEn ? 'EBIOS RM Analysis' : 'Analyse EBIOS RM'), size: 26 }),
            ],
            spacing: { after: 200 },
        }),

        // Date
        new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
                new TextRun({ text: isEn ? 'Generation date: ' : 'Date de g\u00e9n\u00e9ration : ', bold: true, size: 22 }),
                new TextRun({ text: date, size: 22 }),
            ],
            spacing: { after: 1800 },
        }),

        // Footer
        new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
                new TextRun({
                    text: isEn ? `${generatedBy} — EBIOS RM Risk Management Tool` : `${generatedBy} — Outil de gestion des risques EBIOS RM`,
                    size: 18,
                    color: DOCX_COLORS.secondary,
                    italics: true,
                }),
            ],
        }),
    ];
}

// ──────────────────────────────────────────────
// HELPER: TABLE OF CONTENTS
// ──────────────────────────────────────────────
export function buildTableOfContents(title = "Sommaire") {
    return new TableOfContents(title, {
        hyperlink: true,
        headingStyleRange: "1-3",
    });
}

// ──────────────────────────────────────────────
// HELPER: SECTION TITLE
// ──────────────────────────────────────────────

export function buildSectionTitle(text, level = 1, options = {}) {
    const sizes = { 1: 48, 2: 32, 3: 26 };
    const titleColor = options.color || DOCX_COLORS.primary;
    return new Paragraph({
        heading: level === 1 ? HeadingLevel.HEADING_1 : level === 2 ? HeadingLevel.HEADING_2 : HeadingLevel.HEADING_3,
        children: [
            new TextRun({
                text,
                bold: true,
                size: sizes[level] || 28,
                color: titleColor,
            }),
        ],
        spacing: { before: level === 1 ? 800 : 400, after: 300 },
    });
}

// ──────────────────────────────────────────────
// HELPER: BODY PARAGRAPH
// ──────────────────────────────────────────────

export function buildParagraph(text, options = {}) {
    const { bold = false, italic = false, color = DOCX_COLORS.dark, size = 22, indent = 0 } = options;
    return new Paragraph({
        children: [
            new TextRun({ text: text || '', bold, italics: italic, size, color }),
        ],
        indent: indent ? { left: convertInchesToTwip(indent) } : undefined,
        spacing: { after: 150 },
    });
}

// ──────────────────────────────────────────────
// HELPER: BULLET POINT
// ──────────────────────────────────────────────

export function buildBullet(text, level = 0) {
    return new Paragraph({
        children: [
            new TextRun({ text: `${'  '.repeat(level)}• ${text}`, size: 22, color: DOCX_COLORS.dark }),
        ],
        spacing: { after: 100 },
    });
}

// ──────────────────────────────────────────────
// HELPER: INFO BOX (colored left-border paragraph)
// ──────────────────────────────────────────────

export function buildInfoBox(label, value, color = DOCX_COLORS.primary) {
    return new Paragraph({
        children: [
            new TextRun({ text: `${label} : `, bold: true, size: 22, color }),
            new TextRun({ text: value || '—', size: 22, color: DOCX_COLORS.dark }),
        ],
        spacing: { after: 150 },
        border: {
            left: { color, size: 12, style: BorderStyle.SINGLE, space: 8 },
        },
        indent: { left: convertInchesToTwip(0.25) },
    });
}

// ──────────────────────────────────────────────
// HELPER: TABLE
// ──────────────────────────────────────────────

/**
 * Build a styled table.
 * @param {string[]|Array} headers   - Column header labels (strings or {content, color, bgColor})
 * @param {string[][]|Array[]} rows  - 2D array of cell values (strings or {content, color, bgColor})
 * @param {number[]} [columnWidths]  - Optional DXA widths per column
 * @param {number} [totalWidth]      - Optional total table width in DXA
 */
export function buildTable(headers, rows, columnWidths = null, totalWidth = 9360) {
    const colW = columnWidths || headers.map(() => Math.floor(totalWidth / headers.length));

    const headerRow = new TableRow({
        tableHeader: true,
        children: headers.map((h, i) => {
            const headerContent = typeof h === 'object' && h !== null ? h : { content: h };
            const headerColor = headerContent.color || DOCX_COLORS.white;
            const headerBg = headerContent.bgColor || DOCX_COLORS.headerBg;
            return new TableCell({
                width: { size: colW[i], type: WidthType.DXA },
                shading: { fill: headerBg, type: ShadingType.CLEAR },
                children: [
                    new Paragraph({
                        alignment: AlignmentType.CENTER,
                        children: [
                            new TextRun({ text: headerContent.content || String(h), bold: true, size: 20, color: headerColor }),
                        ],
                    }),
                ],
                margins: { top: 80, bottom: 80, left: 120, right: 120 },
            });
        }),
    });

    const dataRows = rows.map((row, rowIdx) =>
        new TableRow({
            children: row.map((cell, colIdx) => {
                const cellObj = typeof cell === 'object' && cell !== null && cell.content !== undefined
                    ? cell
                    : { content: String(cell ?? '—') };
                const cellColor = cellObj.color || DOCX_COLORS.dark;
                const cellBg = cellObj.bgColor || (rowIdx % 2 === 1 ? DOCX_COLORS.altRowBg : '');
                return new TableCell({
                    width: { size: colW[colIdx], type: WidthType.DXA },
                    shading: cellBg
                        ? { fill: cellBg, type: ShadingType.CLEAR }
                        : {},
                    children: String(cellObj.content ?? '—').split('\n').map(line =>
                        new Paragraph({
                            children: [
                                new TextRun({ text: line.trim(), size: 20, color: cellColor }),
                            ],
                            spacing: { after: 40 },
                        })
                    ),
                    margins: { top: 60, bottom: 60, left: 120, right: 120 },
                });
            }),
        })
    );

    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [headerRow, ...dataRows],
    });
}

// ──────────────────────────────────────────────
// HELPER: RED-ACCENTED CELL/HEADER OBJECTS
// ──────────────────────────────────────────────

/**
 * Create a red-accented header object for buildTable.
 */
export function redHeader(content) {
    return content;
}

/**
 * Create a red-accented cell object for buildTable.
 * @param {string} content - Cell text content
 * @param {boolean} [alt=false] - Whether this is an alternating row (darker bg)
 */
export function redCell(content, alt = false) {
    return content;
}

/**
 * Create a section title with red accent (for new content).
 */
export function buildRedSectionTitle(text, level = 1) {
    return buildSectionTitle(text, level);
}

/**
 * Build a table where all headers have red background (for new content).
 * @param {string[]} headers       - Column header labels (plain strings, will be red-accented)
 * @param {string[][]} rows        - 2D array of cell values
 * @param {number[]} [columnWidths] - Optional DXA widths per column
 */
export function buildRedTable(headers, rows, columnWidths = null, totalWidth = 9360) {
    return buildTable(headers, rows, columnWidths, totalWidth);
}

// ──────────────────────────────────────────────
// HELPER: PAGE BREAK
// ──────────────────────────────────────────────

export function buildPageBreak() {
    return new Paragraph({
        children: [new PageBreak()],
    });
}

// ──────────────────────────────────────────────
// HELPER: SEPARATOR LINE (visual)
// ──────────────────────────────────────────────

export function buildSeparator() {
    return new Paragraph({
        children: [
            new TextRun({ text: '─'.repeat(80), color: DOCX_COLORS.secondary, size: 16 }),
        ],
        spacing: { before: 200, after: 200 },
    });
}

// ──────────────────────────────────────────────
// HELPER: MODES OPERATOIRES FLOW VISUALIZATION
// ──────────────────────────────────────────────

const PHASE_COLORS = {
    CONNAITRE: '3B82F6',
    RENTRER: 'EF4444',
    TROUVER: 'F59E0B',
    EXPLOITER: '10B981',
    AGIR: '8B5CF6',
};

/**
 * Build a visual flowchart-like representation of modes op\u00e9ratoires.
 * Each mode is rendered as a styled table showing the sequence of elementary actions.
 * @param {Array} modesOperatoires  - Array of mode objects with { path, vraisemblance, name }
 * @param {Array} elementaryActions - Array of elementary action objects
 * @param {Object} [options]        - { lang: 'fr' | 'en' }
 * @returns {Array}                 - Array of Paragraph/Table elements to push into the doc body
 */
export function buildModesOperatoiresFlow(modesOperatoires, elementaryActions, options = {}) {
    const lang = options.lang || 'fr';
    const elements = [];

    if (!modesOperatoires || modesOperatoires.length === 0 || !elementaryActions || elementaryActions.length === 0) {
        return elements;
    }

    // Only retenu (retained) modes are included in the report
    const filteredModes = modesOperatoires.filter((m) => m && m.retained !== false);
    if (filteredModes.length === 0) return elements;

    // Build a map for quick lookup
    const actionMap = new Map();
    elementaryActions.forEach(a => {
        const id = a.elementaryActionId || a.id;
        if (id) actionMap.set(id, a);
    });

    const sectionTitle = lang === 'en'
        ? 'Graphical Visualization of Operational Modes'
        : 'Visualisation Graphique des Modes Op\u00e9ratoires';

    elements.push(new Paragraph({
        spacing: { before: 300, after: 200 },
        children: [
            new TextRun({ text: sectionTitle, bold: true, size: 24, color: DOCX_COLORS.primary }),
        ],
    }));

    filteredModes.forEach((mode, modeIdx) => {
        if (!mode.path || mode.path.length === 0) return;

        // Mode header
        const v = mode.vraisemblance || 0;
        const modeLabel = lang === 'en'
            ? `Mode ${modeIdx + 1} — Likelihood: ${v}/4`
            : `Mode op\u00e9ratoire ${modeIdx + 1} — Vraisemblance : ${v}/4`;

        elements.push(new Paragraph({
            spacing: { before: 200, after: 100 },
            children: [
                new TextRun({ text: modeLabel, bold: true, size: 22, color: DOCX_COLORS.dark }),
            ],
        }));

        if (mode.description) {
            elements.push(new Paragraph({
                spacing: { after: 100 },
                children: [
                    new TextRun({ text: mode.description, size: 20, color: DOCX_COLORS.secondary, italics: true }),
                ],
            }));
        }

        // Build flow table: one column per action in the path
        const actions = mode.path
            .map(actionId => actionMap.get(actionId))
            .filter(Boolean);

        if (actions.length === 0) return;

        // Table header row: phase badges
        const headerCells = actions.map(action => {
            const phase = (action.phase || '').toUpperCase();
            const phaseColor = PHASE_COLORS[phase] || '6B7280';
            return new TableCell({
                width: { size: Math.floor(9360 / Math.min(actions.length, 4)), type: WidthType.DXA },
                shading: { fill: phaseColor, type: ShadingType.CLEAR },
                children: [
                    new Paragraph({
                        alignment: AlignmentType.CENTER,
                        children: [
                            new TextRun({ text: phase || '—', bold: true, size: 18, color: 'FFFFFF' }),
                        ],
                    }),
                ],
                margins: { top: 40, bottom: 40, left: 60, right: 60 },
            });
        });

        // Technique row
        const techniqueCells = actions.map(action => {
            const technique = action.technique || action.name || '—';
            return new TableCell({
                width: { size: Math.floor(9360 / Math.min(actions.length, 4)), type: WidthType.DXA },
                shading: { fill: 'F8FAFC', type: ShadingType.CLEAR },
                children: [
                    new Paragraph({
                        alignment: AlignmentType.CENTER,
                        children: [
                            new TextRun({ text: technique, bold: true, size: 18, color: DOCX_COLORS.dark }),
                        ],
                    }),
                ],
                margins: { top: 60, bottom: 60, left: 60, right: 60 },
            });
        });

        // Description row
        const descCells = actions.map(action => {
            const desc = action.description || '—';
            return new TableCell({
                width: { size: Math.floor(9360 / Math.min(actions.length, 4)), type: WidthType.DXA },
                children: [
                    new Paragraph({
                        children: [
                            new TextRun({ text: desc, size: 16, color: DOCX_COLORS.secondary }),
                        ],
                    }),
                ],
                margins: { top: 40, bottom: 40, left: 60, right: 60 },
            });
        });

        // Metrics row (PS / DT / V)
        const metricsCells = actions.map(action => {
            const ps = action.successProbability || action.ps || '—';
            const dt = action.technicalDifficulty || action.dt || '—';
            const vr = action.vraisemblance || '—';
            const metricsText = `PS:${ps}  DT:${dt}  V:${vr}`;
            return new TableCell({
                width: { size: Math.floor(9360 / Math.min(actions.length, 4)), type: WidthType.DXA },
                shading: { fill: 'F1F5F9', type: ShadingType.CLEAR },
                children: [
                    new Paragraph({
                        alignment: AlignmentType.CENTER,
                        children: [
                            new TextRun({ text: metricsText, size: 16, color: DOCX_COLORS.secondary, bold: true }),
                        ],
                    }),
                ],
                margins: { top: 40, bottom: 40, left: 60, right: 60 },
            });
        });

        // If too many actions, split into multiple rows of max 4 columns
        const maxCols = 4;
        for (let i = 0; i < actions.length; i += maxCols) {
            const sliceH = headerCells.slice(i, i + maxCols);
            const sliceT = techniqueCells.slice(i, i + maxCols);
            const sliceD = descCells.slice(i, i + maxCols);
            const sliceM = metricsCells.slice(i, i + maxCols);

            // Add arrow between chunks
            if (i > 0) {
                elements.push(new Paragraph({
                    alignment: AlignmentType.CENTER,
                    spacing: { before: 60, after: 60 },
                    children: [new TextRun({ text: '▼', size: 28, color: DOCX_COLORS.secondary })],
                }));
            }

            elements.push(new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                rows: [
                    new TableRow({ children: sliceH }),
                    new TableRow({ children: sliceT }),
                    new TableRow({ children: sliceD }),
                    new TableRow({ children: sliceM }),
                ],
            }));
        }
    });

    return elements;
}

// ──────────────────────────────────────────────
// HELPER: EMPTY LINE
// ──────────────────────────────────────────────

export function buildSpacer(after = 200) {
    return new Paragraph({ text: '', spacing: { after } });
}

// ──────────────────────────────────────────────
// HELPER: SEVERITY BADGE TEXT
// ──────────────────────────────────────────────

export function getSeverityLabel(severity, lang = 'fr') {
    const isEn = lang === 'en';
    const mapFr = {
        critical: 'Critique',
        high: '\u00c9lev\u00e9e',
        medium: 'Moyenne',
        low: 'Faible',
        'tr\u00e8s \u00e9lev\u00e9': 'Tr\u00e8s \u00e9lev\u00e9',
    };
    const mapEn = {
        critical: 'Critical',
        high: 'High',
        medium: 'Medium',
        low: 'Low',
        'tr\u00e8s \u00e9lev\u00e9': 'Very High',
    };
    const map = isEn ? mapEn : mapFr;
    return map[String(severity).toLowerCase()] || severity || '—';
}

export function getLikelihoodLabel(likelihood, lang = 'fr') {
    const isEn = lang === 'en';
    const mapFr = {
        critical: 'Quasi certain',
        critique: 'Quasi certain',
        high: 'Très vraisemblable',
        'élevé': 'Très vraisemblable',
        'élevée': 'Très vraisemblable',
        medium: 'Vraisemblable',
        moyen: 'Vraisemblable',
        low: 'Peu vraisemblable',
        faible: 'Peu vraisemblable',
    };
    const mapEn = {
        critical: 'Almost certain',
        critique: 'Almost certain',
        high: 'Very likely',
        'élevé': 'Very likely',
        'élevée': 'Very likely',
        medium: 'Likely',
        moyen: 'Likely',
        low: 'Unlikely',
        faible: 'Unlikely',
    };
    const map = isEn ? mapEn : mapFr;
    // Handle numeric values (1-4 scale)
    const numMapFr = { 1: 'Peu vraisemblable', 2: 'Vraisemblable', 3: 'Très vraisemblable', 4: 'Quasi certain' };
    const numMapEn = { 1: 'Unlikely', 2: 'Likely', 3: 'Very likely', 4: 'Almost certain' };
    const numMap = isEn ? numMapEn : numMapFr;
    const str = String(likelihood);
    if (numMap[parseInt(str)]) return numMap[parseInt(str)];
    return map[str.toLowerCase()] || likelihood || '—';
}

// ──────────────────────────────────────────────
// DOCUMENT BUILDER
// ──────────────────────────────────────────────

/**
 * Build a complete Document with standard header/footer.
 * @param {string} documentTitle   - For header
 * @param {Paragraph[]} sections   - Array of sections (each section = array of paragraphs)
 * @param {string} footerLeft      - Left footer text
 * @param {Uint8Array|ArrayBuffer} [watermarkImage] - Optional watermark image to display behind text
 */
export function buildDocument(documentTitle, sections, footerLeft = 'EBIOS RM', watermarkImage = null, options = {}) {
    return new Document({
        creator: 'EBIOSens — ACG Cybersecurity',
        title: documentTitle,
        description: 'Rapport EBIOS RM g\u00e9n\u00e9r\u00e9 par EBIOSens',
        features: {
            updateFields: true,
        },
        sections: sections.map((children, idx) => ({
            properties: {
                page: {
                    size: options.orientation === 'landscape' ? {
                        orientation: PageOrientation.LANDSCAPE,
                        width: convertInchesToTwip(11.69),
                        height: convertInchesToTwip(8.27),
                    } : undefined,
                    margin: {
                        top: convertInchesToTwip(1),
                        right: convertInchesToTwip(1),
                        bottom: convertInchesToTwip(1),
                        left: convertInchesToTwip(1),
                    },
                },
            },
            headers:
                idx > 0
                    ? {
                        default: new Header({
                            children: [
                                new Paragraph({
                                    alignment: AlignmentType.RIGHT,
                                    children: [
                                        new TextRun({
                                            text: documentTitle,
                                            size: 18,
                                            color: DOCX_COLORS.secondary,
                                            italics: true,
                                        }),
                                        ...(watermarkImage ? [
                                            new ImageRun({
                                                data: watermarkImage,
                                                transformation: {
                                                    width: options.orientation === 'landscape' ? 1123 : 794,
                                                    height: options.orientation === 'landscape' ? 794 : 1123,
                                                },
                                                floating: {
                                                    horizontalPosition: {
                                                        offset: 0,
                                                    },
                                                    verticalPosition: {
                                                        offset: 0,
                                                    },
                                                    wrap: {
                                                        type: 'none',
                                                    },
                                                    behindDocument: true,
                                                },
                                            })
                                        ] : [])
                                    ],
                                }),
                            ],
                        }),
                    }
                    : undefined,
            footers: {
                default: new Footer({
                    children: [
                        new Paragraph({
                            alignment: AlignmentType.CENTER,
                            children: [
                                new TextRun({ text: `${footerLeft}  |  Page `, size: 18, color: DOCX_COLORS.secondary }),
                                new TextRun({ children: [PageNumber.CURRENT], size: 18 }),
                                new TextRun({ text: ' / ', size: 18, color: DOCX_COLORS.secondary }),
                                new TextRun({ children: [PageNumber.TOTAL_PAGES], size: 18 }),
                            ],
                        }),
                    ],
                }),
            },
            children,
        })),
    });
}

// ──────────────────────────────────────────────
// MAIN EXPORT FUNCTION
// ──────────────────────────────────────────────

/**
 * Generate and download a DOCX file.
 * @param {Document} doc      - docx Document instance
 * @param {string} filename   - Desired filename (without extension)
 */
export async function downloadDocx(doc, filename) {
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${filename}.docx`);
}
